import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import dynamic from 'next/dynamic';

import type { TicketGroup } from '@interfaces/ticketevolution/tikets';
import { useCurrencyContext } from '@contexts/currency-context';
import { useTicketsStore } from '@store/tickets-store';
import { formatCurrencyV2 } from '@utils/format-currency-v2';
import { DualRangeSlider, clamp } from '@v2/ui/Slider';

import {
  getTicketPriceDistribution,
  getTicketPriceRange,
  getTicketsInPriceRange,
} from './ticket-price-distribution';

const TicketPriceDistributionChart = dynamic(
  () =>
    import('./TicketPriceDistributionChart').then(
      (module) => module.TicketPriceDistributionChart
    ),
  {
    ssr: false,
    loading: () => (
      <div className="mb-1 h-[92px] w-full animate-pulse rounded-md bg-gray-100" />
    ),
  }
);

function toDisplayedPrice(
  usd: number,
  usdRange: { min: number; max: number },
  displayRange: { min: number; max: number },
  factor: number
) {
  if (usd <= usdRange.min) return displayRange.min;
  if (usd >= usdRange.max) return displayRange.max;
  return Math.round(usd * factor);
}

function toUsdPrice(
  display: number,
  currentUsd: number,
  usdRange: { min: number; max: number },
  displayRange: { min: number; max: number },
  factor: number
) {
  if (display <= displayRange.min) return usdRange.min;
  if (display >= displayRange.max) return usdRange.max;
  if (display === toDisplayedPrice(currentUsd, usdRange, displayRange, factor))
    return currentUsd;
  return display / factor;
}

export function TicketPriceRangeSlider({
  ticketsList,
}: {
  ticketsList: TicketGroup[];
}) {
  const { priceRate, currency } = useCurrencyContext();
  const factor = Number.isFinite(priceRate) && priceRate > 0 ? priceRate : 1;
  const priceRange = useTicketsStore((state) => state.priceRange);
  const setPriceRange = useTicketsStore((state) => state.setPriceRange);

  const displayTickets = useMemo(
    () =>
      ticketsList.map(({ retail_price }) => ({
        retail_price: retail_price * factor,
      })),
    [ticketsList, factor]
  );
  const usdRange = useMemo(
    () => getTicketPriceRange(ticketsList),
    [ticketsList]
  );
  const displayRange = useMemo(
    () => getTicketPriceRange(displayTickets),
    [displayTickets]
  );

  const [draft, setDraft] = useState<[number, number]>(() => [
    toDisplayedPrice(priceRange[0], usdRange, displayRange, factor),
    toDisplayedPrice(priceRange[1], usdRange, displayRange, factor),
  ]);

  useEffect(() => {
    setDraft([
      toDisplayedPrice(priceRange[0], usdRange, displayRange, factor),
      toDisplayedPrice(priceRange[1], usdRange, displayRange, factor),
    ]);
  }, [priceRange[0], priceRange[1], usdRange, displayRange, factor]);

  const formatDisplayPrice = useCallback(
    (price: number) => formatCurrencyV2(price, 1, currency, false, false),
    [currency]
  );

  const effectiveUsdDraft = useMemo<[number, number]>(
    () => [
      toUsdPrice(draft[0], priceRange[0], usdRange, displayRange, factor),
      toUsdPrice(draft[1], priceRange[1], usdRange, displayRange, factor),
    ],
    [draft, priceRange, usdRange, displayRange, factor]
  );
  const selectedMin = clamp(draft[0], displayRange.min, displayRange.max);
  const selectedMax = clamp(draft[1], displayRange.min, displayRange.max);
  const sliderValue: [number, number] = [
    Math.min(selectedMin, selectedMax),
    Math.max(selectedMin, selectedMax),
  ];
  const priceDistribution = useMemo(
    () =>
      getTicketPriceDistribution(
        displayTickets,
        displayRange,
        12,
        formatDisplayPrice
      ),
    [displayTickets, displayRange, formatDisplayPrice]
  );
  const ticketsInRange = useMemo(
    () => getTicketsInPriceRange(ticketsList, effectiveUsdDraft),
    [ticketsList, effectiveUsdDraft]
  );

  return (
    <section aria-labelledby="ticket-price-range-heading">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3
            id="ticket-price-range-heading"
            className="text-base font-bold text-gray-800"
          >
            Price range
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Price per ticket ({currency.toUpperCase()})
          </p>
        </div>
        <div
          className="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold tabular-nums text-primary-600"
          aria-live="polite"
        >
          {ticketsInRange.toLocaleString()}{' '}
          {ticketsInRange === 1 ? 'option' : 'options'}
        </div>
      </div>

      <TicketPriceDistributionChart
        priceDistribution={priceDistribution}
        selectedMax={selectedMax}
        selectedMin={selectedMin}
      />

      <div className="mb-2 flex items-center justify-between text-sm font-semibold tabular-nums text-gray-800">
        <output aria-label="Minimum selected ticket price">
          {formatDisplayPrice(selectedMin)}
        </output>
        <output aria-label="Maximum selected ticket price">
          {formatDisplayPrice(selectedMax)}
        </output>
      </div>

      <DualRangeSlider
        min={displayRange.min}
        max={displayRange.max}
        step={1}
        minGap={1}
        value={sliderValue}
        minThumbAriaLabel="Minimum ticket price"
        maxThumbAriaLabel="Maximum ticket price"
        formatAriaValue={formatDisplayPrice}
        onValueChange={setDraft}
        onValueCommit={(next) => {
          startTransition(() => {
            setPriceRange([
              toUsdPrice(
                next[0],
                priceRange[0],
                usdRange,
                displayRange,
                factor
              ),
              toUsdPrice(
                next[1],
                priceRange[1],
                usdRange,
                displayRange,
                factor
              ),
            ]);
          });
        }}
      />
    </section>
  );
}
